import { headerOffset } from "components/basic/header";
import Profile from "./pages/profile";
import ServiceOffering from "./pages/serviceOffering";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Box, IconButton, useMediaQuery, useTheme } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Typography from "components/basic/typography";
import SidebarMenu from "./sidebarMenu";
import { accountRole, dashboardFeaturesFor } from "./dashboardFeatures";
import Onboarding from "./pages/onboarding";
import {
  missingRequiredDocuments,
  onboardingOpen,
} from "./pages/onboarding/helpers";
import Leads from "./pages/leads";
import Team from "./pages/team";
import Calendar from "./pages/calendar";
import Meetings from "./pages/meetings";
import Message from "./pages/message";
import { useMeQuery, VendorVerification } from "generated/graphql";
import Quotes from "./pages/quotes";
import Support from "./pages/support";
import Admin from "./pages/admin";
import { useSupportUnreadCount } from "./pages/support/supportApi";
import { useTranslation } from "react-i18next";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation("dashboard");
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));
  const [menuOpen, setMenuOpen] = useState(false);
  const [{ data }] = useMeQuery();
  const userType = accountRole(data?.me);
  const isAdmin = data?.me?.isAdmin ?? false;
  const canEditApplication =
    data?.me?.vendor?.verificationStatus === VendorVerification.Rejected;
  const badges = {
    support: useSupportUnreadCount(false, Boolean(data?.me)),
    admin: useSupportUnreadCount(true, isAdmin),
  };

  // Get current feature from URL path
  const pathSegments = location.pathname.split("/");
  const currentFeature = pathSegments[2] || ""; // /dashboard/profile -> 'profile'

  const vendor = data?.me?.vendor;
  const onboarding = Boolean(vendor && onboardingOpen(vendor));
  const documentsMissing = Boolean(vendor && missingRequiredDocuments(vendor));

  // Redirect to profile (or onboarding, until it's finished) if no feature is
  // specified
  useEffect(() => {
    if (
      location.pathname === "/dashboard" ||
      location.pathname === "/dashboard/"
    ) {
      navigate(onboarding ? "/dashboard/onboarding" : "/dashboard/profile", {
        replace: true,
      });
    }
    // Analytics moved under Admin; keeps old bookmarks working.
    if (location.pathname.startsWith("/dashboard/analytics")) {
      navigate("/dashboard/admin?tab=analytics", { replace: true });
    }
  }, [location.pathname, navigate, onboarding]);

  // Vendors owing an expiry date can only finish onboarding or ask support.
  useEffect(() => {
    if (
      documentsMissing &&
      !["onboarding", "support"].includes(currentFeature)
    ) {
      navigate("/dashboard/onboarding", { replace: true });
    }
  }, [documentsMissing, currentFeature, navigate]);

  // Function to handle feature navigation
  const handleFeatureChange: React.Dispatch<React.SetStateAction<string>> = (
    feature,
  ) => {
    const newFeature =
      typeof feature === "function" ? feature(currentFeature) : feature;
    navigate(`/dashboard/${newFeature}`);
  };

  const currentItem = dashboardFeaturesFor(
    userType,
    isAdmin,
    canEditApplication,
  ).find((item) => item.path === currentFeature);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: `calc(100dvh - ${headerOffset(1)})`,
      }}
    >
      {/* Below md the rail is a drawer, so it needs something to open it. */}
      {isCompact && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
            paddingX: 1,
            paddingY: 0.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <IconButton
            aria-label={t("menu.open")}
            onClick={() => setMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h5"
            noWrap
            style={{ fontWeight: "bold", textTransform: "uppercase" }}
          >
            {currentItem && t(currentItem.label)}
          </Typography>
        </Box>
      )}

      <SidebarMenu
        selectedFeature={currentFeature}
        setSelectedFeature={handleFeatureChange}
        userType={userType}
        isAdmin={isAdmin}
        canEditApplication={canEditApplication}
        badges={badges}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      />

      {/* Main content area */}
      <Box
        sx={{
          flex: 1,
          // Without this a flex child refuses to shrink past its content, which
          // is how the wide tables used to push the whole pane sideways.
          minWidth: 0,
          minHeight: 0,
          overflow: "auto",
          scrollbarWidth: "thin",
        }}
      >
        {(() => {
          switch (currentFeature) {
            case "onboarding":
              return vendor ? <Onboarding /> : <Profile />;
            case "profile":
              return <Profile />;
            case "application":
              return canEditApplication ? <Profile application /> : <Profile />;
            case "serviceOfferings":
              return <ServiceOffering />;
            case "leads":
              return <Leads />;
            case "messages":
              return <Message />;
            case "calendar":
              return <Calendar />;
            case "meetings":
              return <Meetings />;
            // Built but unused, so it is off the rail while the route stays.
            case "team":
              return <Team />;
            // user-type routes
            case "quotes":
              return <Quotes />;
            case "support":
              return <Support />;
            // Guarded again server-side: the resolvers reject anyone not in
            // ADMIN_EMAILS, so reaching this route by URL shows an error
            // rather than data.
            case "admin":
              return <Admin />;
            default:
              return (
                <div>
                  {t("feature_in_progress", { feature: currentFeature })}
                </div>
              );
          }
        })()}
      </Box>
    </Box>
  );
};

export default Dashboard;
